import { useState } from 'react';
import { disablePush, enablePush, PushDenied, sendTestPush, usePushState, type PushState } from '../lib/push';
import { useToast } from '../lib/toast';
import { Sheet } from './Sheet';

const IOS_INSTALL =
  'Auf iPhone und iPad bekommt Closetly nur Mitteilungen, wenn es auf dem Home-Bildschirm liegt: in Safari auf Teilen tippen, dann „Zum Home-Bildschirm“. Closetly von dort öffnen und hier einschalten.';
const DENIED =
  'Mitteilungen sind für Closetly blockiert. Erlaube sie in den Einstellungen (iPhone: Einstellungen → Mitteilungen → Closetly), dann hier einschalten.';

/**
 * Switch for this device. `compact` is the one-liner under the price alert
 * in the item form; it only shows while notifications are not on yet.
 */
export function PushControls({ compact = false }: { compact?: boolean }) {
  const [state, refresh] = usePushState();
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await action();
      toast(done);
    } catch (err) {
      toast(
        err instanceof PushDenied
          ? 'Ohne Erlaubnis keine Mitteilungen.'
          : navigator.onLine
            ? 'Das hat nicht geklappt. Versuch es nochmal.'
            : 'Dafür braucht es eine Verbindung.',
      );
    } finally {
      setBusy(false);
      refresh();
    }
  }

  const enable = () => run(enablePush, 'Mitteilungen sind auf diesem Gerät eingeschaltet');

  if (compact) {
    if (state === 'off')
      return (
        <p className="hint">
          Damit du es mitbekommst:{' '}
          <button type="button" className="linkbtn" disabled={busy} onClick={enable}>
            Mitteilungen einschalten
          </button>
        </p>
      );
    if (state === 'ios-install' || state === 'denied')
      return <p className="hint">{state === 'denied' ? DENIED : IOS_INSTALL}</p>;
    return null;
  }

  const text: Record<PushState, string> = {
    loading: '',
    on: 'Auf diesem Gerät eingeschaltet.',
    off: 'Auf diesem Gerät ausgeschaltet. Jedes Gerät schaltest du einzeln ein.',
    denied: DENIED,
    'ios-install': IOS_INSTALL,
    unsupported: 'Dieser Browser kann keine Mitteilungen empfangen.',
  };
  return (
    <>
      <p className="hint" role="status">
        {text[state]}
      </p>
      <div className="push-actions">
        {state === 'off' && (
          <button type="button" className="btn ink" disabled={busy} onClick={enable}>
            Auf diesem Gerät einschalten
          </button>
        )}
        {state === 'on' && (
          <>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const { sent } = await sendTestPush();
                  if (!sent) throw new Error('not delivered');
                }, 'Test gesendet')
              }
            >
              Test senden
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => run(disablePush, 'Mitteilungen sind auf diesem Gerät ausgeschaltet')}
            >
              Ausschalten
            </button>
          </>
        )}
      </div>
    </>
  );
}

export function NotifySheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet open onClose={onClose} onSubmit={onClose} labelledBy="notifyTitle">
      <h2 id="notifyTitle">Mitteilungen</h2>
      <p>
        Closetly meldet sich, wenn ein Teil, dessen Preis du beobachtest, günstiger wird, deinen Zielpreis erreicht
        oder wieder verfügbar ist. Geprüft wird etwa alle sechs Stunden.
      </p>
      <PushControls />
      <div className="sheet-actions">
        <div className="right">
          <button type="submit" className="btn">
            Fertig
          </button>
        </div>
      </div>
    </Sheet>
  );
}
